import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { inviteBulkServiceFn, inviteServiceFn } from '@account_module/components/InviteForm';
import { Button } from '@components/Button';
import { RoleSelect, OptionType } from '@account_module/components/RoleSelect';
import { useMegaphoneOnboardContext } from '@account_module/context/MegaphoneOnboardContext';
import { useNotification } from '@insights_module/hooks/useNotification';
import { CloseIcon } from '@icons/CloseIcon';
import { Info } from '@icons/Info';

const Container = tw.div`w-full max-w-[490px] bg-white border border-solid border-ruler rounded-lg overflow-hidden`;
const FormHeader = tw.div`w-full flex items-center justify-between border-b border-solid border-ruler`;
const FormTitle = tw.h3`text-left w-full max-w-[490px] bg-white text-base px-4 py-[10px]`;
const ControlsContainer = tw.div`flex items-center justify-between mt-[32px]`;
const ButtonsContainer = tw.div`w-full flex items-center justify-end gap-2`;
const FormButton = tw(
  Button.Primary
)`w-auto h-auto px-[10px] py-[6px] text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed`;
const CancelButton = tw(
  FormButton
)`border border-solid border-card-border bg-transparent text-primary`;
const CloseButton = tw(FormButton)`bg-transparent text-primary`;
const InputLabel = tw.label`text-[12px] font-bold leading-none`;
const FormFooter = tw.div`w-full flex items-center justify-between p-4 bg-[#E0E9F5] text-[#294E82] text-[12px] text-left`;
const inputStyle = tw`w-full block border border-solid border-ruler rounded-lg p-2 my-[6px]`;

type InviteMemeberFormType = {
  email: string;
  permission_type: string;
};

const options: OptionType[] = [
  {
    key: 'admin',
    label: 'Admin',
    title: 'Admin',
    description: 'Can do everything as user can and can manage content and team',
  },
];

const inviteMemberFormShema = yup.object().shape({
  email: yup.string().required('Email is required'),
});

export const InviteMemberForm = ({ organizationId }: { organizationId: string | undefined }) => {
  const { t } = useTranslation();
  const { notify, NTFC_TYPE } = useNotification();
  const { setIsInviteModalVisible } = useMegaphoneOnboardContext();
  const { register, handleSubmit, formState } = useForm<InviteMemeberFormType>({
    defaultValues: {
      permission_type: 'admin',
    },
    resolver: yupResolver(inviteMemberFormShema),
  });
  const { isValid } = formState;

  const closeModal = () => setIsInviteModalVisible(false);

  const onSubmit = async (formData: InviteMemeberFormType) => {
    const emails = formData.email.split(',').map((email) => email.trim());

    if (organizationId) {
      if (emails.length > 1) {
        inviteBulkServiceFn(organizationId, formData, emails)
          .then(() =>
            notify({
              type: NTFC_TYPE.INFO,
              message: 'Collaborators invitations sent',
            })
          )
          .catch((error) =>
            notify({
              type: NTFC_TYPE.DANGER,
              message: error.message,
            })
          )
          .finally(closeModal);

        return;
      }

      inviteServiceFn(organizationId, formData)
        .then(() =>
          notify({
            type: NTFC_TYPE.INFO,
            message: 'Collaborator invitations sent',
          })
        )
        .catch((error) =>
          notify({
            type: NTFC_TYPE.DANGER,
            message: error.message,
          })
        )
        .finally(closeModal);
    }
  };

  return (
    <Container>
      <FormHeader>
        <FormTitle>{t('account_module.onboard_page.invite_member_form.title')}</FormTitle>
        <CloseButton onClick={closeModal}>
          <CloseIcon tw='fill-primary' />
        </CloseButton>
      </FormHeader>
      <form onSubmit={handleSubmit(onSubmit)} tw='p-4'>
        <InputLabel htmlFor='email'>
          {t('account_module.onboard_page.invite_member_form.input_label')}
        </InputLabel>
        <input
          type='text'
          {...register('email')}
          css={[inputStyle]}
          placeholder={t('account_module.onboard_page.invite_member_form.input_placeholder')}
        />
        <span tw='text-text-muted-2 text-[11px]'>
          {t('account_module.onboard_page.invite_member_form.input_info')}
        </span>

        <ControlsContainer>
          {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
          <RoleSelect options={options} onChange={() => {}} disabled={true} />
          <ButtonsContainer>
            <CancelButton type='button' onClick={closeModal}>
              {t('account_module.onboard_page.invite_member_form.cancel')}
            </CancelButton>
            <FormButton type='submit' disabled={!isValid}>
              {t('account_module.onboard_page.invite_member_form.invite')}
            </FormButton>
          </ButtonsContainer>
        </ControlsContainer>
      </form>
      <FormFooter>
        <Info tw='stroke-[#294E82] min-w-[16px] mr-1' />
        <span>{t('account_module.onboard_page.invite_member_form.footer_text')}</span>
      </FormFooter>
    </Container>
  );
};
